import React, {useContext, useEffect, useMemo, useState} from "react";
import {
	Autocomplete,
	Button,
	CardActions,
	CircularProgress,
	DialogActions,
	DialogContentText,
	Paper,
	TextField,
	Tooltip,
	Typography
} from "@mui/material";
import AccessTimeTwoToneIcon from "@mui/icons-material/AccessTimeTwoTone";
import AvTimerTwoToneIcon from "@mui/icons-material/AvTimerTwoTone";
import BusinessTwoToneIcon from "@mui/icons-material/BusinessTwoTone";
import CancelPresentationTwoToneIcon from "@mui/icons-material/CancelPresentationTwoTone";
import DoneOutlineTwoToneIcon from "@mui/icons-material/DoneOutlineTwoTone";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import ForwardTwoToneIcon from "@mui/icons-material/ForwardTwoTone";
import LabelTwoToneIcon from "@mui/icons-material/LabelTwoTone";
import PersonTwoToneIcon from "@mui/icons-material/PersonTwoTone";
import ReplyTwoToneIcon from "@mui/icons-material/ReplyTwoTone";
import SaveTwoToneIcon from "@mui/icons-material/SaveTwoTone";
import ThumbDownAltTwoToneIcon from "@mui/icons-material/ThumbDownAltTwoTone";
import ThumbUpAltTwoToneIcon from "@mui/icons-material/ThumbUpAltTwoTone";
import clsx from "clsx";
import {AnagraficaContext, AppContext, CategoriesContext, TicketContext} from "../../../../App";
import {addNoteToDiario, resetGetAnagfrafica} from "../../../../services/AnagraficaServices";
import {getTicketHistory, searchTicket, updateTicket} from "../../../../services/TicketServices";
import {setOpenSnackbar} from "../../../../services/AppServices";
import {getAccountInfo, getCategoryByLabel} from "../../../../utils/GenericUtils";
import {spAslCode} from "../../../../utils/dictionaries/Generics";
import {categoriesV2} from "../../../../utils/dictionaries/Categories";
import SimpleDialog from "../../../widgets/dialogs/SimpleDialog";
import AutocompleteSelect from "../../../widgets/inputs/AutocompleteSelect";

const hiddenTags = [
	"#MOSTRA_USER",
	"#IN_ATTESA_DI_RISPOSTA",
	"#RICHIESTA_CHIAMATA",
	"#INOLTRATO",
];

const priorityOptions = [
	{value: "Bassa", label: "Bassa", color: "var(--atl-green)"},
	{value: "Media", label: "Media", color: "var(--atl-yellow)"},
	{value: "Alta", label: "Alta", color: "var(--atl-red)"},
	{value: "Urgente", label: "Urgente", color: "#9c27b0"},
];

const levelOptions = [
	{value: 0, label: "Base", color: "var(--atl-blue)"},
	{value: 1, label: "Intermedio", color: "var(--atl-blue)"},
	{value: 2, label: "Avanzato", color: "var(--atl-blue-dark)"},
	{value: 3, label: "Supervisore", color: "var(--atl-teal)"},
	{value: 4, label: "Amministratore", color: "var(--atl-red)"},
];

const blockOptions = [
	{value: "inactive", label: "Disattiva", color: "var(--atl-green)"},
	{value: "active", label: "Attiva", color: "var(--atl-red)"},
];

const formatTicketDate = (value) => {
	if (!value) return "Non disponibile";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return String(value);
	}

	return new Intl.DateTimeFormat("it-IT", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(date);
};

const extractTags = (value = "") => String(value)
	.match(/#(\S*)/g)
	|| [];

const getOperationalTags = (value = "") => extractTags(value)
	.filter((tag) => !hiddenTags.includes(tag) && tag !== "#BLOCCA_RISPOSTE");

const normalizeTag = (value) => {
	const normalized = String(value || "")
		.trim()
		.replace(/^#+/, "")
		.replace(/\s+/g, "_")
		.toUpperCase();

	return normalized ? `#${normalized}` : "";
};

const uniqueTags = (values) => [...new Set(values.map(normalizeTag).filter(Boolean))];

const actionToneClasses = (classes, tone) => ({
	primary: classes.ticketDetailActionPrimary,
	success: classes.ticketDetailActionSuccess,
	warning: classes.ticketDetailActionWarning,
	danger: classes.ticketDetailActionDanger,
}[tone] || classes.ticketDetailActionPrimary);

const SummaryFact = ({classes, icon, label, value}) => (
	<div className={classes.ticketDetailFact}>
		<span className={classes.ticketDetailFactIcon}>{icon}</span>
		<span className={classes.ticketDetailFactLabel}>{label}</span>
		<strong className={classes.ticketDetailFactValue}>{value || "Non disponibile"}</strong>
	</div>
);

const ActionButton = ({children, disabled = false, icon, onClick, ticketClasses, title, tone = "primary"}) => (
	<Tooltip title={title}>
		<span>
			<Button
				className={clsx(ticketClasses.ticketDetailActionButton, actionToneClasses(ticketClasses, tone))}
				disabled={disabled}
				disableElevation
				endIcon={icon}
				onClick={onClick}
				size="small"
				variant="contained"
			>
				<b>{children}</b>
			</Button>
		</span>
	</Tooltip>
);

const SelectValue = ({classes, option}) => (
	<span
		className={classes.ticketSummarySelectValue}
		style={{"--ticket-select-color": option?.color || "var(--atl-blue)"}}
	>
		<LabelTwoToneIcon/>
		{option?.label || "Non indicato"}
	</span>
);

const SummarySection = ({actions, children, classes, subtitle, title}) => (
	<section className={classes.ticketSummarySection}>
		<div className={classes.ticketSummarySectionHeader}>
			<div>
				<Typography component="h3" className={classes.ticketSummarySectionTitle}>
					{title}
				</Typography>
				{subtitle && (
					<Typography className={classes.ticketSummarySectionSubtitle}>
						{subtitle}
					</Typography>
				)}
			</div>
			{actions}
		</div>
		{children}
	</section>
);

const toSelectOption = (value) => ({value: value.label, label: value.label});

const renderColoredOption = (classes) => (option) => (
	<SelectValue classes={classes} option={option}/>
);

const renderColoredAdornment = (classes) => (option) => option?.color ? (
	<LabelTwoToneIcon
		className={classes.ticketSummarySelectAdornment}
		style={{"--ticket-select-color": option.color}}
	/>
) : null;

const TicketSummaryManager = ({assignee, classes, ticket}) => {
	const ticketContext = useContext(TicketContext);
	const appContext = useContext(AppContext);
	const anagraficaContext = useContext(AnagraficaContext);
	const categoriesContext = useContext(CategoriesContext);
	const [category, setCategory] = useState("");
	const [subcategory, setSubcategory] = useState("");
	const [tags, setTags] = useState([]);
	const [priority, setPriority] = useState("");
	const [level, setLevel] = useState(0);
	const [block, setBlock] = useState(false);
	const [forwardOpen, setForwardOpen] = useState(false);
	const [forwardDst, setForwardDst] = useState("");
	const [resolutionMode, setResolutionMode] = useState(null);
	const [pendingMessage, setPendingMessage] = useState("");

	const categoryOptions = useMemo(() => Object.values(categoriesContext.categoriesV3 || {})
		.sort((a, b) => a.label.localeCompare(b.label))
		.map(toSelectOption), [categoriesContext.categoriesV3]);

	const selectedCategory = useMemo(
		() => getCategoryByLabel(category, categoriesContext.categoriesV3 || {}) || {},
		[category, categoriesContext.categoriesV3]
	);

	const subcategoryOptions = useMemo(
		() => selectedCategory.subCategories?.filter((value) => value.enabled).map(toSelectOption) || [],
		[selectedCategory.subCategories]
	);
	const forwardOptions = useMemo(
		() => Object.entries(spAslCode).map(([value, label]) => ({value, label})),
		[]
	);

	useEffect(() => {
		setCategory(ticket?.categoria || "");
		setSubcategory(ticket?.sottoCategoria || "");
		setTags(uniqueTags(extractTags(ticket?.dettaglioRichiesta).filter((tag) => !hiddenTags.includes(tag) && tag !== "#BLOCCA_RISPOSTE")));
		setPriority(ticket?.priority || "");
		setLevel(ticket?.livello ?? 0);
		setBlock(extractTags(ticket?.dettaglioRichiesta).includes("#BLOCCA_RISPOSTE"));
	}, [ticket]);

	useEffect(() => {
		if (!category || !subcategoryOptions.length) return;
		if (!subcategoryOptions.some((value) => value.label === subcategory)) {
			setSubcategory("");
		}
	}, [category, subcategory, subcategoryOptions]);

	useEffect(() => {
		if (!pendingMessage || ticketContext.state.loadingAddHistory) return;

		if (ticketContext.state.errorAddHistory) {
			setOpenSnackbar("error", ticketContext.state.errorAddHistory, appContext.dispatch);
			setPendingMessage("");
			return;
		}

		setOpenSnackbar("success", pendingMessage, appContext.dispatch);
		searchTicket({id: ticket.id}, ticketContext.dispatch);
		getTicketHistory({id: ticket.id}, ticketContext.dispatch);
		resetGetAnagfrafica(anagraficaContext.dispatch);
		setPendingMessage("");
	}, [
		pendingMessage,
		ticket,
		ticketContext.state.loadingAddHistory,
		ticketContext.state.errorAddHistory,
		ticketContext.dispatch,
		appContext.dispatch,
		anagraficaContext.dispatch,
	]);

	const buildRequestText = () => {
		const originalText = String(ticket?.dettaglioRichiesta || "").trim();
		const tagsToReplace = new Set([
			...getOperationalTags(originalText),
			"#BLOCCA_RISPOSTE",
		]);
		const preservedText = originalText
			.split(/\s+/)
			.filter((chunk) => !tagsToReplace.has(chunk))
			.join(" ")
			.trim();

		return [
			preservedText,
			...uniqueTags(tags),
			block ? "#BLOCCA_RISPOSTE" : "",
		].filter(Boolean).join(" ");
	};

	const buildNote = (note) => [
		block ? "#BLOCCA_RISPOSTE" : "",
		...uniqueTags(tags),
		note,
	].filter(Boolean).join(" ");

	const buildTicketPayload = (overrides = {}) => ({
		...ticket,
		categoria: category || ticket.categoria,
		sottoCategoria: subcategory || ticket.sottoCategoria,
		dettaglioRichiesta: buildRequestText(),
		priority: priority || ticket.priority,
		livello: level,
		ticketId: ticket.id,
		stato: ticket.stato,
		allegati: null,
		rispostaUtente: false,
		...overrides,
	});

	const runUpdate = (overrides, message, isMail = false) => {
		setPendingMessage(message);
		updateTicket(
			buildTicketPayload(overrides),
			ticketContext.dispatch,
			"NULL",
			isMail,
			"NULL",
			ticket.idSoggetto
		);
	};

	const saveSummary = () => {
		runUpdate(
			{note: buildNote("")},
			"Riepilogo ticket aggiornato"
		);
	};

	const ticketIsAnonymous = ticket?.soggetto?.codF === "UTENTE_ANONIMO";
	const isEsenzioneTicket = category === categoriesV2[1]?.label;
	const requiresResolutionChoice = category === categoriesV2[12]?.label;
	const saving = Boolean(pendingMessage) && ticketContext.state.loadingAddHistory;

	const handleResolve = () => {
		if (isEsenzioneTicket) {
			runUpdate(
				{stato: "Risolto", esito: "Positivo", note: buildNote("#RISOLTO")},
				"Ticket segnato come risolto"
			);
			return;
		}

		if (requiresResolutionChoice) {
			setResolutionMode("resolve");
			return;
		}

		runUpdate(
			{stato: "Risolto", esito: "Positivo", note: buildNote("#RISOLTO")},
			"Ticket segnato come risolto"
		);
	};

	const handleMail = () => {
		if (requiresResolutionChoice) {
			setResolutionMode("mail");
			return;
		}

		runUpdate(
			{stato: "Risolto", note: buildNote("#MOSTRA_USER")},
			"Ticket aggiornato e mail inviata",
			true
		);
	};

	const confirmForward = () => {
		if (!forwardDst) {
			setOpenSnackbar("warning", "Seleziona una destinazione", appContext.dispatch);
			return;
		}

		runUpdate(
			{
				dst: forwardDst,
				livello: 0,
				stato: "Nuovo",
				note: buildNote(`#INOLTRATO a ${spAslCode[forwardDst] || forwardDst} da ${getAccountInfo().alias}`),
			},
			"Ticket inoltrato"
		);
		setForwardOpen(false);
	};

	const confirmResolution = (positive) => {
		runUpdate(
			{
				stato: "Risolto",
				esito: positive ? "Positivo" : "Negativo",
				note: buildNote(resolutionMode === "mail" ? "#MOSTRA_USER" : "#RISOLTO"),
			},
			resolutionMode === "mail" ? "Ticket risolto e mail inviata" : "Ticket segnato come risolto",
			resolutionMode === "mail"
		);
		setResolutionMode(null);
	};

	return (
		<React.Fragment>
			<Paper className={clsx(classes.ticketDetailPanel, classes.ticketDetailInfoPanel)} component="section" elevation={0}>
				<div className={classes.ticketDetailInfoHeader}>
					<div>
						<Typography component="h2" className={classes.ticketDetailPanelTitle}>
							Riepilogo ticket
						</Typography>
						<Typography className={classes.ticketDetailPanelSubtitle}>
							Info, classificazione e azioni operative del ticket.
						</Typography>
					</div>
				</div>

				<div className={classes.ticketSummaryBody}>
					<SummarySection
						classes={classes}
						title="Identificativi"
					>
						<div className={classes.ticketSummaryFactsGrid}>
							<SummaryFact classes={classes} icon={<AccessTimeTwoToneIcon/>} label="Apertura" value={formatTicketDate(ticket.dataCreazione)}/>
							<SummaryFact classes={classes} icon={<AccessTimeTwoToneIcon/>} label="Ultima modifica" value={formatTicketDate(ticket.dataUltimaModifica)}/>
							<SummaryFact classes={classes} icon={<PersonTwoToneIcon/>} label="Assegnatario" value={assignee}/>
							<SummaryFact classes={classes} icon={<BusinessTwoToneIcon/>} label="Destinazione" value={spAslCode[ticket?.dst] || ticket?.dst || "Non assegnata"}/>
						</div>
					</SummarySection>

					<SummarySection
						classes={classes}
						title="Classificazione"
					>
						<div className={classes.ticketSummaryControlsGrid}>
							<AutocompleteSelect
								className={classes.ticketSummaryField}
								disableClearable
								label="Categoria"
								onChange={setCategory}
								options={categoryOptions}
								size="small"
								value={category}
							/>

							<AutocompleteSelect
								className={classes.ticketSummaryField}
								disableClearable
								label="Sottocategoria"
								onChange={setSubcategory}
								options={subcategoryOptions}
								size="small"
								value={subcategory}
							/>

							<Autocomplete
								className={classes.ticketSummaryTags}
								filterSelectedOptions
								freeSolo
								multiple
								onChange={(event, newValue) => setTags(uniqueTags(newValue))}
								options={[]}
								PaperComponent={(props) => <Paper {...props} className={classes.ticketSolidMenuPaper}/>}
								size="small"
								value={tags}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Tag"
										placeholder="#TAG"
										size="small"
									/>
								)}
							/>
						</div>
					</SummarySection>

					<SummarySection
						classes={classes}
						title="Gestione"
					>
						<div className={classes.ticketSummaryMetaGrid}>
							<AutocompleteSelect
								className={classes.ticketSummaryField}
								disableClearable
								label="Priorita"
								onChange={setPriority}
								options={priorityOptions}
								renderOptionContent={renderColoredOption(classes)}
								renderSelectedAdornment={renderColoredAdornment(classes)}
								size="small"
								value={priority}
							/>

							<AutocompleteSelect
								className={classes.ticketSummaryField}
								disableClearable
								label="Livello"
								onChange={setLevel}
								options={levelOptions}
								renderOptionContent={renderColoredOption(classes)}
								renderSelectedAdornment={renderColoredAdornment(classes)}
								size="small"
								value={level}
							/>

							<AutocompleteSelect
								className={classes.ticketSummaryField}
								disableClearable
								label="Blocco richiesta"
								onChange={(value) => setBlock(value === "active")}
								options={blockOptions}
								renderOptionContent={renderColoredOption(classes)}
								renderSelectedAdornment={renderColoredAdornment(classes)}
								size="small"
								value={block ? "active" : "inactive"}
							/>
						</div>
					</SummarySection>

					<SummarySection
						classes={classes}
						title="Azioni"
					>
						<CardActions className={classes.detailActions}>
							<ActionButton
								ticketClasses={classes}
								title="Salva classificazione, priorita, livello e blocco richiesta."
								icon={saving ? <CircularProgress size={16}/> : <SaveTwoToneIcon/>}
								disabled={saving}
								onClick={saveSummary}
								tone="success"
							>
								Salva
							</ActionButton>
							<ActionButton
								ticketClasses={classes}
								title="Il ticket torna in coda Nuovo e viene inoltrato a una nuova destinazione."
								icon={<ForwardTwoToneIcon/>}
								disabled={ticketIsAnonymous || isEsenzioneTicket}
								onClick={() => setForwardOpen(true)}
							>
								Inoltra
							</ActionButton>
							<ActionButton
								ticketClasses={classes}
								title="Il ticket diventa Preso in carico."
								icon={<AvTimerTwoToneIcon/>}
								disabled={ticketIsAnonymous}
								onClick={() => runUpdate({stato: "Preso in carico", note: buildNote("#PRESO_IN_CARICO")}, "Ticket preso in carico")}
								tone="warning"
							>
								Preso in carico
							</ActionButton>
							<ActionButton
								ticketClasses={classes}
								title="Il ticket viene messo in attesa di risposta."
								icon={<ReplyTwoToneIcon/>}
								disabled={ticketIsAnonymous}
								onClick={() => runUpdate({stato: "In attesa", note: buildNote("#IN_ATTESA_DI_RISPOSTA")}, "Ticket messo in attesa")}
							>
								In attesa
							</ActionButton>
							<ActionButton
								ticketClasses={classes}
								title="Segna il ticket come risolto."
								icon={<DoneOutlineTwoToneIcon/>}
								onClick={handleResolve}
								tone="success"
							>
								Risolto
							</ActionButton>
							{isEsenzioneTicket && (
								<ActionButton
									ticketClasses={classes}
									title="Segna il ticket come non esentabile e aggiunge la nota nel diario paziente."
									icon={<CancelPresentationTwoToneIcon/>}
									disabled={ticketIsAnonymous}
									onClick={() => {
										addNoteToDiario({
											text: "PAZIENTE NON ESENTABILE",
											soggetto: ticket?.soggetto?.idSoggetto,
										}, ticket?.soggetto?.idSoggetto, anagraficaContext.dispatch);
										runUpdate(
											{stato: "Risolto", esito: "Negativo", note: buildNote("#MOSTRA_USER PAZIENTE_NON_ESENTABILE")},
											"Ticket segnato come non esentabile",
											true
										);
									}}
									tone="danger"
								>
									Non esentabile
								</ActionButton>
							)}
							<ActionButton
								ticketClasses={classes}
								title="Segna il ticket come risolto e invia una mail di aggiornamento all'utente."
								icon={<EmailTwoToneIcon/>}
								disabled={ticketIsAnonymous}
								onClick={handleMail}
							>
								Mail
							</ActionButton>
						</CardActions>
					</SummarySection>
				</div>
			</Paper>

			{forwardOpen && (
				<SimpleDialog
					title="A chi vuoi inoltrare il ticket?"
					maxWidth="sm"
					open={forwardOpen}
					handleClose={() => setForwardOpen(false)}
					content={(
						<React.Fragment>
							<DialogContentText>
								Scegli una destinazione operativa per rimettere il ticket in coda.
							</DialogContentText>
							<AutocompleteSelect
								className={classes.ticketSummaryDialogField}
								disableClearable
								label="Nuova destinazione"
								onChange={setForwardDst}
								options={forwardOptions}
								size="small"
								value={forwardDst}
							/>
						</React.Fragment>
					)}
					actions={(
						<DialogActions>
							<Button onClick={() => setForwardOpen(false)} size="small">
								Annulla
							</Button>
							<Button
								className={clsx(classes.ticketDetailActionButton, classes.ticketDetailActionSuccess)}
								disableElevation
								onClick={confirmForward}
								size="small"
								variant="contained"
							>
								<b>Conferma</b>
							</Button>
						</DialogActions>
					)}
				/>
			)}

			{resolutionMode && (
				<SimpleDialog
					title="Com'e stato risolto?"
					maxWidth="sm"
					open={Boolean(resolutionMode)}
					handleClose={() => setResolutionMode(null)}
					content={(
						<React.Fragment>
							<DialogContentText>
								Indica l'esito conclusivo della richiesta.
							</DialogContentText>
							<div className={classes.ticketDetailResolutionActions}>
								<Button
									onClick={() => confirmResolution(true)}
									disableElevation
									variant="contained"
									endIcon={<ThumbUpAltTwoToneIcon/>}
									className={clsx(classes.ticketDetailActionButton, classes.ticketDetailActionSuccess)}
								>
									Positivamente
								</Button>
								<Button
									onClick={() => confirmResolution(false)}
									disableElevation
									variant="contained"
									endIcon={<ThumbDownAltTwoToneIcon/>}
									className={clsx(classes.ticketDetailActionButton, classes.ticketDetailActionDanger)}
								>
									Negativamente
								</Button>
							</div>
						</React.Fragment>
					)}
				/>
			)}
		</React.Fragment>
	);
};

export default React.memo(TicketSummaryManager);
